import { useCallback, useState } from 'react';
import {
  PROFILE_HUB_CONTACT_EMAIL,
} from '../constants/profileHubLinks';
import { useFinePointerDevice } from '../hooks/useFinePointerDevice';
import { useI18n } from '../i18n';
import { copyTextToClipboard, gmailComposeUrl, mailtoUrl } from '../utils/emailCompose';
import { AppBuildInfoTrigger } from './AppBuildInfoTrigger';
import { AuthClearAppDataButton } from './AuthClearAppDataButton';
import { LastErrorInspectTrigger } from './LastErrorInspectTrigger';
import styles from './ProfileHubContactPanel.module.css';

type ProfileHubContactPanelProps = {
  email?: string;
};

export function ProfileHubContactPanel({
  email = PROFILE_HUB_CONTACT_EMAIL,
}: ProfileHubContactPanelProps) {
  const { t } = useI18n();
  const showDesktopEmailActions = useFinePointerDevice();
  const [copied, setCopied] = useState(false);
  const trimmedEmail = email.trim();
  const mailAppHref = mailtoUrl(trimmedEmail);
  const gmailHref = gmailComposeUrl(trimmedEmail, {
    subject: 'Placy support',
  });

  const onCopyEmail = useCallback(async () => {
    const ok = await copyTextToClipboard(trimmedEmail);
    if (!ok) {
      return;
    }
    setCopied(true);
    window.setTimeout(() => setCopied(false), 2000);
  }, [trimmedEmail]);

  return (
    <div className={styles.panel}>
      <div className={styles.emailRow}>
        <a
          className={styles.primaryLink}
          href={mailAppHref}
          onClick={(event) => event.stopPropagation()}
        >
          <ProfileEmailIcon />
          <span dir="ltr">{trimmedEmail}</span>
        </a>
        <div className={styles.utilityIcons}>
          <AuthClearAppDataButton />
          <LastErrorInspectTrigger />
          <AppBuildInfoTrigger />
        </div>
      </div>
      {showDesktopEmailActions ? (
        <div className={styles.actions}>
          <a
            className={[styles.actionBtn, styles.gmailBtn].join(' ')}
            href={gmailHref}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={t('profile.hub.contactGmail')}
            title={t('profile.hub.contactGmail')}
            onClick={(event) => event.stopPropagation()}
          >
            <GmailIcon />
          </a>
          <button
            type="button"
            className={styles.actionBtn}
            onClick={(event) => {
              event.stopPropagation();
              void onCopyEmail();
            }}
          >
            {copied ? t('profile.hub.contactCopied') : t('profile.hub.contactCopyEmail')}
          </button>
        </div>
      ) : null}
    </div>
  );
}

function GmailIcon() {
  return (
    <svg viewBox="0 0 48 48" width="22" height="22" aria-hidden>
      <path
        fill="#4caf50"
        d="M45,16.9v19.2c0,1.7-1.3,3-3,3H6c-1.7,0-3-1.3-3-3V16.9"
      />
      <path
        fill="#1e88e5"
        d="M45,16.9L24,30.1L3,16.9V10.7c0-1.7,1.3-3,3-3h36c1.7,0,3,1.3,3,3V16.9z"
      />
      <path
        fill="#e53935"
        d="M45,10.7v6.2L24,30.1L3,16.9v-6.2c0-1.7,1.3-3,3-3h36C43.7,7.7,45,9,45,10.7z"
      />
    </svg>
  );
}

function ProfileEmailIcon() {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <path d="M3 7l9 6 9-6" />
    </svg>
  );
}
